/**
 * useLandingSeasonHero – resolves the seasonal hero image for the landing page.
 *
 * Priority:
 * 1. ?season= URL override
 * 2. Calendar season (Europe/Stockholm)
 *
 * Fetches landing_season_overlays for the resolved season.
 * Returns the public URL for the hero image, or null (caller falls back to static default).
 */

import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentSeason, parseSeasonParam, type Season } from '@/lib/season';

interface LandingSeasonHero {
  /** Public URL of the seasonal hero image, or null if no overlay / no asset. */
  imageUrl: string | null;
  /** Alt text from the media asset, if available. */
  altText: string | null;
  /** The season that was resolved (after override / calendar). */
  activeSeason: Season;
  /** Whether the query is still loading. */
  isLoading: boolean;
}

export function useLandingSeasonHero(): LandingSeasonHero {
  const [searchParams] = useSearchParams();
  const paramSeason = parseSeasonParam(searchParams.get('season'));
  const activeSeason: Season = paramSeason ?? getCurrentSeason();

  const { data, isLoading } = useQuery({
    queryKey: ['landing-season-hero', activeSeason],
    queryFn: async () => {
      // 1. Fetch the overlay row for this season
      const { data: overlay, error: overlayErr } = await supabase
        .from('landing_season_overlays')
        .select('hero_media_asset_id')
        .eq('season', activeSeason)
        .maybeSingle();

      if (overlayErr) throw overlayErr;
      if (!overlay?.hero_media_asset_id) return null;

      // 2. Fetch media asset to get the image path
      const { data: asset, error: assetErr } = await supabase
        .from('media_assets')
        .select('path_640, path_1280, path_320, alt_text, bucket')
        .eq('id', overlay.hero_media_asset_id)
        .maybeSingle();

      if (assetErr) throw assetErr;
      if (!asset) return null;

      // Pick best resolution for hero (prefer 1280, then 640, then 320)
      const path = asset.path_1280 ?? asset.path_640 ?? asset.path_320;
      if (!path) return null;

      const { data: urlData } = supabase.storage
        .from(asset.bucket)
        .getPublicUrl(path);

      return {
        imageUrl: urlData.publicUrl,
        altText: asset.alt_text,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 min
  });

  return {
    imageUrl: data?.imageUrl ?? null,
    altText: data?.altText ?? null,
    activeSeason,
    isLoading,
  };
}
